import { uniqBy } from "lodash";
import { useMemo } from "react";
import { useProducts } from "../../admin/products/useProducts";
import { useProductsSellingPrices } from "../../calculations/calculationVersionEdit/useProductsSellingPrices";
import { useProductsWithPrices } from "../../calculations/calculationVersionEdit/useProductsWithPrices";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { CalculationDraft } from "../CalculationDraft";

export function useCalculationProducts(draft: CalculationDraft) {
  const { products: allProducts } = useProducts();
  const { products, currency } = draft;
  const { startDate, margin } = draft.basicData;
  const effectiveStartDate = startDate ?? dayjs();
  const productDetailsMap = useMemo(() => new Map(allProducts.map((p) => [p.id, p])), [allProducts]);
  const priceQueryInput = useMemo(
    () =>
      uniqBy(
        products
          .filter((p) => p.id)
          .map((p) => ({
            id: p.id,
            dateFrom: effectiveStartDate.add(p.daysShift, "days").format(ISO_DATE_FORMAT),
            dateTo: effectiveStartDate.add(p.daysShift + 1, "days").format(ISO_DATE_FORMAT),
          })),
        (p) => `${p.id}:${p.dateFrom}`,
      ),
    [products, effectiveStartDate],
  );
  const totalParticipants = draft.basicData.peopleCount + draft.basicData.freePeopleCount;
  const { prices } = useProductsSellingPrices(priceQueryInput, totalParticipants);

  const availableProducts = useMemo(
    () => (currency ? allProducts.filter((p) => p.currency === currency) : allProducts),
    [allProducts, currency],
  );
  const productsWithDate = products.map((it) => ({
    ...it,
    date: effectiveStartDate.add(it.daysShift, "days").format(ISO_DATE_FORMAT),
  }));
  const { productsWithPrices } = useProductsWithPrices(
    productsWithDate,
    currency ?? "PLN",
    margin,
    availableProducts,
    prices,
    false,
  );
  return { productDetailsMap, availableProducts, productsWithPrices };
}
